import React from "react";
import { LegalLayout } from "./TermsPage";

export default function PrivacyPolicyPage() {
  return (
    <LegalLayout title="Privacy Policy" testid="privacy-page">
      <p className="text-zinc-600">
        This Privacy Policy explains how Iceberg Digital (&ldquo;we&rdquo;, &ldquo;us&rdquo;,
        &ldquo;our&rdquo;) collects, uses, stores, and shares personal information
        when you use our marketing reporting platform (the &ldquo;Service&rdquo;).
        We comply with the Australian Privacy Principles in the Privacy Act
        1988 (Cth) and, where applicable, the GDPR.
      </p>

      <Section title="1. Information we collect">
        <ul className="list-disc pl-5 space-y-1">
          <li><strong>Account data</strong> &mdash; name, email, role, company affiliation, and credentials used to sign in.</li>
          <li><strong>Client &amp; campaign data</strong> &mdash; marketing performance data you upload via CSV/Excel or that we fetch from connected third-party platforms on your authorisation.</li>
          <li><strong>Usage data</strong> &mdash; logs of pages viewed, features used, and timestamps for security and product improvement.</li>
          <li><strong>Device data</strong> &mdash; browser type, operating system, IP address, and similar diagnostics.</li>
          <li><strong>Communications</strong> &mdash; messages you send us via email or in-app channels.</li>
        </ul>
      </Section>

      <Section title="2. How we use information">
        <ul className="list-disc pl-5 space-y-1">
          <li>Provide, operate, and maintain the Service.</li>
          <li>Generate the dashboards, charts, PDF reports, and AI-assisted recommendations you request.</li>
          <li>Authenticate users and protect against fraud, abuse, and unauthorised access.</li>
          <li>Respond to support requests and communicate platform updates.</li>
          <li>Improve and develop new features (using aggregated, de-identified data wherever possible).</li>
          <li>Comply with legal obligations and enforce our terms.</li>
        </ul>
      </Section>

      <Section title="3. AI processing">
        <p>
          The Service uses large language models (&ldquo;LLMs&rdquo;) to draft commentary
          and recommendations. When you trigger an AI feature, the campaign
          metrics and the prompt template are sent to the LLM provider over an
          encrypted connection. We use enterprise-grade providers that do not
          train on customer data by default. AI output is generated
          automatically, may contain errors, and should always be reviewed.
        </p>
      </Section>

      <Section title="4. Third-party data sources">
        <p>
          With your authorisation we connect to and fetch data from third
          parties on your behalf, including (non-exhaustively): Google Ads,
          Google Analytics 4, Google Search Console, Google Business Profile,
          YouTube Analytics, Meta Marketing API, WhatsApp Business, LinkedIn
          Marketing, Microsoft Advertising / Bing Webmaster / Clarity, X
          (Twitter), Pinterest, OpenAI, ProductReview.com.au, Trustpilot,
          SendGrid, and Twilio. Each provider has its own privacy policy and
          retention rules; we recommend you review them.
        </p>
      </Section>

      <Section title="5. How we share information">
        <p>We do not sell personal information. We share information only:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>With <strong>service providers</strong> that host or support the Service (cloud hosting, database, email delivery, error monitoring), under written contracts.</li>
          <li>With <strong>your authorised users</strong> &mdash; e.g. agency staff and client contacts on the same account.</li>
          <li>To <strong>comply with law</strong>, respond to lawful requests, protect rights, or in connection with a corporate transaction.</li>
        </ul>
      </Section>

      <Section title="6. Data storage &amp; security">
        <p>
          Customer data is stored in encrypted databases hosted in cloud
          regions that we select for proximity, redundancy, and compliance.
          Access is restricted on a least-privilege basis, all administrative
          actions are logged, and tokens for third-party integrations are
          encrypted at rest. While no system is perfectly secure, we use
          commercially reasonable safeguards and regularly review them.
        </p>
      </Section>

      <Section title="7. Retention">
        <p>
          We retain personal information for as long as your account is
          active, then for a reasonable export window plus a short archive
          period to satisfy legal, accounting, and reporting obligations.
          You may request earlier deletion by contacting us.
        </p>
      </Section>

      <Section title="8. Your rights">
        <p>You have the right to:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>Request access to the personal information we hold about you.</li>
          <li>Request correction of inaccurate or incomplete information.</li>
          <li>Request deletion of your information, subject to lawful retention.</li>
          <li>Withdraw consent for any optional processing at any time.</li>
          <li>Lodge a complaint with the Office of the Australian Information Commissioner (OAIC) or your local data-protection authority.</li>
        </ul>
        <p className="mt-2">
          To exercise these rights, email{" "}
          <a href="mailto:privacy@icebergdigital.com.au" className="text-[#002FA7] hover:underline">
            privacy@icebergdigital.com.au
          </a>.
        </p>
      </Section>

      <Section title="9. Cookies &amp; local storage">
        <p>
          We use a small number of essential cookies and browser local-storage
          entries to keep you signed in, remember preferences, and provide
          security features. We do not use third-party advertising or
          behavioural-tracking cookies inside the platform.
        </p>
      </Section>

      <Section title="10. International transfers">
        <p>
          Some of our hosting and AI providers operate in regions outside
          Australia. Where personal information is transferred internationally,
          we use providers with appropriate safeguards (e.g. Standard
          Contractual Clauses) to ensure equivalent protection.
        </p>
      </Section>

      <Section title="11. Children">
        <p>
          The Service is intended for business users and is not directed to
          individuals under 18. We do not knowingly collect information from
          children.
        </p>
      </Section>

      <Section title="12. Changes to this Policy">
        <p>
          We may update this Privacy Policy from time to time. Material
          changes will be notified through the Service or by email. The
          &ldquo;Last updated&rdquo; date at the top reflects the most recent revision.
        </p>
      </Section>

      <Section title="13. Contact">
        <p>
          Privacy questions? Reach our Privacy Officer at{" "}
          <a href="mailto:privacy@icebergdigital.com.au" className="text-[#002FA7] hover:underline">
            privacy@icebergdigital.com.au
          </a>
          .
        </p>
      </Section>
    </LegalLayout>
  );
}

function Section({ title, children }) {
  return (
    <section>
      <h2 className="font-display font-bold text-lg text-zinc-900 mb-2">{title}</h2>
      <div className="text-sm">{children}</div>
    </section>
  );
}
